import React from "react";
import { VariableModel } from "../models/VariableModel";
import { includes, unescape } from "./lodash";

/**
 * Returns the scrollable container div for the survey.
 */
export const getContainerDiv = () =>
    document.getElementsByClassName("survey-engine")[0] as HTMLDivElement;

export const formatChoiceText = (text: string) => unescape(removeHtmlTags(text));

/**
 * Strips HTML tags from a string.
 */
export const escapeHtml = (str: string, detectTabs?: boolean) => {
    let escaped = str;
    escaped = escaped.replace(/[\u00A0-\u9999<>\&]/gi, (m) => `&#${m.charCodeAt(0)};`);
    escaped = escaped.replace(/\n/g, "<br>");
    if (detectTabs) escaped = escaped.replace(/\t|    /g, "&nbsp;&nbsp;&nbsp;&nbsp;");
    return escaped;
};

/**
 * Remove all HTML tags from a string.
 */
export const removeHtmlTags = (str: string) =>
    str ? str.replace(/<br>/g, " ").replace(/<\/?[^>]*>/g, "") : "";

/**
 * Returns true if the string is null, undefined or empty.
 */
export const isNullOrEmpty = (str: string) => str == null || str.length === 0;

/**
 * Optional theme color: null, undefined, non-string, empty, or whitespace-only → undefined
 * so callers keep legacy fallbacks (e.g. theme.SecondaryColor).
 */
export const themeOptionalColorString = (value: unknown): string | undefined => {
    if (value == null || typeof value !== "string") return undefined;
    const trimmed = value.trim();
    if (isNullOrEmpty(trimmed)) return undefined;
    return trimmed;
};

/**
 * Numeric theme setting: theme values arrive from the server in an untyped `settings` bag, so a
 * stringified "0" must still read as the number 0 — otherwise strict `=== 0` guards silently flip.
 * Null, undefined, empty and non-numeric fall back so a missing value never reads as 0.
 *
 * No observed theme sends strings for these keys — this hardens a shape the type permits, rather
 * than fixing a reproduced bug.
 */
export const themeNumber = (value: unknown, fallback: number): number => {
    if (value == null || value === "") return fallback;
    const parsed = typeof value === "number" ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

/**
 * Returns true if the number is null, undefined or NaN.
 */
export const isNullOrNaN = (dec: number) => dec == null || isNaN(dec);

export const isEmptyOrNaN = (v: any) => !v || v === "" || Number.isNaN(v);

/**
 * Generates a hash code from a string.
 */
export const hashCode = (s: string) => {
    if (!s) return "";
    return s.split("").reduce((a, b) => {
        a = (a << 5) - a + b.charCodeAt(0);
        return a & a;
    }, 0);
};

/**
 * Return color from decimal to hexadecimal.
 * @param dec
 */
export const rgbaFromDec = (dec: number) => {
    if (dec === -1) return null;
    const hex = (dec >>> 0).toString(16);
    let rgba = "rgba(";
    for (let i = 0; i < 3; i++) rgba += `${parseInt(hex.slice((i + 1) * 2, (i + 2) * 2), 16)},`;
    rgba += parseInt(hex.slice(0, 2), 16) / 255;
    return `${rgba})`;
};

/**
 * Converts a hexadecimal string "#FFFFFF" to {r, g, b} with 16 bit values or rgb string value.
 */
export const hexToRgb = (hex: string) => {
    // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? {
              r: parseInt(result[1], 16),
              g: parseInt(result[2], 16),
              b: parseInt(result[3], 16),
          }
        : null;
};

/**
 * Converts a rgb array {r, g, b} with 16 bit values to rgb string value.
 */
export const rgbToRgbString = (rgbArr) => {
    return `rgb(${rgbArr.r}, ${rgbArr.g}, ${rgbArr.b})`;
};

/**
 * Converts a rgb string to hexadecimal color string.
 */
export const rgbToHex = (rgb: string) => {
    // Splits rgb values into r, g and b array
    const rgbArr = rgb.split("(")[1].split(")")[0].split(",");

    // Converts r, g and b decimals into hex values
    const b = rgbArr.map((x) => {
        x = parseInt(x).toString(16);
        return x.length === 1 ? `0${x}` : x;
    });

    return `#${b.join("")}`;
};

/** Used to trigger blur events on fields. */
let blurEvent: Event;

/**
 * Triggers blur on the focused field (if any). Used to prevent onMouseDown from preventing blur events.
 * Returns the previously focused element.
 */
export const blurFocusedField = () => {
    if (blurEvent == null) {
        blurEvent = document.createEvent("Events");
        blurEvent.initEvent("blur", false, false);
    }
    const focused = document.activeElement as HTMLElement;
    if (focused != null && includes(["input", "textarea"], focused.tagName.toLowerCase())) {
        focused.dispatchEvent(blurEvent);
        focused.blur();
        return focused;
    }
};

export const nltobr = (str: string) => {
    if (str == null || typeof str !== "string") return str;
    return str
        .replace("\t", "tab//")
        .split("\n")
        .map((l, i) => (
            <span key={i}>
                {i > 0 && <br key={`br_${i}`} />}
                {l.startsWith(" ") && <>&nbsp;</>}
                {l}
            </span>
        ));
};

export const xhrPOST = (
    url: string,
    data: any,
    onLoad: (result: string, error?: any) => void,
    onProgress?: (ev: ProgressEvent) => void,
) => {
    const xhr = new XMLHttpRequest();
    if (onProgress) {
        xhr.addEventListener("progress", onProgress);
    }
    xhr.open("POST", url, true);
    xhr.addEventListener("load", (e) => {
        if (xhr.readyState === 4) {
            if (xhr.status === 200) {
                onLoad(xhr.responseText);
            } else {
                onLoad(null, xhr.statusText);
            }
        }
    });
    xhr.addEventListener("error", (e) => {
        onLoad(null, xhr.statusText);
    });
    xhr.send(JSON.parse(data));
};

export const xhrGET = (
    url: string,
    onLoad: (result: string, error?: any) => void,
    onProgress?: (ev: ProgressEvent) => void,
) => {
    const xhr = new XMLHttpRequest();
    if (onProgress) {
        xhr.addEventListener("progress", onProgress);
    }
    xhr.open("GET", url, true);
    xhr.addEventListener("load", (e) => {
        if (xhr.readyState === 4) {
            if (xhr.status === 200) {
                onLoad(xhr.responseText);
            } else {
                onLoad(null, xhr.statusText);
            }
        }
    });
    xhr.addEventListener("error", (e) => {
        onLoad(null, xhr.statusText);
    });
    xhr.send();
};

export const getFileSize = (size: number) => {
    const units = ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    let remainingSize = size;
    let i = 0;
    while (remainingSize >= 1024) {
        remainingSize /= 1024;
        i += 1;
    }
    return `${remainingSize.toFixed(0)}${units[i]}`;
};

/* eslint-disable */
// @ts-ignore
export const isIE = () => /*@cc_on!@*/ false || !!document.documentMode;
/* eslint-enable */

export const isValueInChoiceList = (value: string, variable: VariableModel) => {
    return variable.choiceList.some((c) => c.value === value);
};
